import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import { useAuth } from '../../../hooks/useAuth';

interface HeaderProps {
  onMenuClick: () => void;
}

interface Notification {
  id: number;
  titre: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'CRITICAL';
  created_at: string;
  read_at: string | null;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const { user, signOut } = useAuth();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  /* ================= LOAD NOTIFICATIONS ================= */

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 30000); // refresh 30s
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await getResources('notifications');
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (e) {
      console.error('Erreur notifications');
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await createResource(`notifications/${id}/read`);
      fetchNotifications();
    } catch {}
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };

  const typeColor = (type: string) => {
    if (type === 'CRITICAL') return 'text-red-600';
    if (type === 'WARNING') return 'text-orange-600';
    return 'text-blue-600';
  };

  return (
    <header className="fixed top-0 right-0 left-0 lg:left-64 h-20 bg-white border-b px-4 lg:px-6 z-30">
      <div className="flex items-center justify-between h-full">

        {/* LEFT */}
        <div className="flex items-center space-x-4">
          <button onClick={onMenuClick} className="lg:hidden p-2 hover:bg-gray-100">
            <i className="ri-menu-line text-2xl"></i>
          </button>
          <h1 className="text-xl font-bold">Tableau de Bord</h1>
        </div>

        {/* RIGHT */}
        <div className="flex items-center space-x-4">

          {/* 🔔 NOTIFICATIONS */}
          <div className="relative">
            <button
              onClick={() => setShowNotif(!showNotif)}
              className="relative p-2 text-gray-500 hover:text-gray-700"
            >
              <i className="ri-notification-3-line text-xl"></i>

              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotif && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNotif(false)} />

                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border z-50">
                  <div className="px-4 py-3 border-b font-semibold">
                    Notifications
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 && (
                      <p className="p-4 text-sm text-gray-500 text-center">
                        Aucune notification
                      </p>
                    )}

                    {notifications.map(n => (
                      <div
                        key={n.id}
                        onClick={() => markAsRead(n.id)}
                        className={`px-4 py-3 border-b cursor-pointer hover:bg-gray-50 ${
                          !n.read_at ? 'bg-blue-50' : ''
                        }`}
                      >
                        <p className={`text-sm font-semibold ${typeColor(n.type)}`}>
                          {n.titre}
                        </p>
                        <p className="text-sm text-gray-700">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(n.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 👤 USER MENU */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50"
            >
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.role?.nom}</p>
              </div>
              <i className="ri-arrow-down-s-line hidden md:block"></i>
            </button>

            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow border z-50">
                  <a href="/profile" className="block px-4 py-2 hover:bg-gray-50">
                    <i className="ri-user-line mr-2"></i> Profil
                  </a>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                  >
                    <i className="ri-logout-box-line mr-2"></i> Déconnexion
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}
